/**
 * Shared UI Components
 * Small reusable building blocks used across all pages.
 * All MUI-based for consistency.
 */
import { memo, useState, useCallback, useRef, useEffect } from "react";
import MuiButton from "@mui/material/Button";
import MuiTextField from "@mui/material/TextField";
import MuiCard from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import MuiChip from "@mui/material/Chip";
import { FONT, MONO, STATUS_COLORS, COLORS } from "../theme/tokens";
import { v } from "../utils/helpers";

// Status color map - uses new design token palette
const STATUS_STYLE = {
  COMPLETED: { bg: "rgba(52,211,153,.10)", color: "#34D399", border: "rgba(52,211,153,.3)" },
  STARTED:   { bg: "rgba(52,211,153,.10)", color: "#34D399", border: "rgba(52,211,153,.3)" },
  FAILED:    { bg: "rgba(248,113,113,.10)", color: "#F87171", border: "rgba(248,113,113,.3)" },
  ERROR:     { bg: "rgba(248,113,113,.10)", color: "#F87171", border: "rgba(248,113,113,.3)" },
  PROCESSING:{ bg: "rgba(75,127,250,.10)",  color: "#4B7FFA", border: "rgba(75,127,250,.3)" },
  RETRY:     { bg: "rgba(251,191,36,.10)",  color: "#FBBF24", border: "rgba(251,191,36,.3)" },
  ESCALATED: { bg: "rgba(248,113,113,.10)", color: "#F87171", border: "rgba(248,113,113,.3)" },
};

// Static style object for StatusBadge - created once, not on each render
const STATUS_BADGE_BASE = {
  display: "inline-flex",
  alignItems: "center",
  height: 22,
  padding: "0 9px",
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "-.01em",
  fontFamily: FONT,
  whiteSpace: "nowrap",
};

// === Status Badge (COMPLETED, FAILED, etc.) - memoized, stable style objects ===
export const StatusBadge = memo(({ status }) => {
  const style = STATUS_STYLE[status];
  if (style) {
    return (
      <span style={{
        ...STATUS_BADGE_BASE,
        background: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
      }}>
        {status}
      </span>
    );
  }
  return (
    <MuiChip
      label={status}
      size="small"
      color={STATUS_COLORS[status] || "default"}
      variant="outlined"
      sx={{ fontSize: 11, fontWeight: 700, height: 22 }}
    />
  );
});

// === Text Input ===
export const Inp = ({ label, value, onChange, type = "text", ph = "", title = "" }) => (
  <MuiTextField
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    type={type}
    placeholder={ph}
    title={title}
    size="small"
    fullWidth
    variant="outlined"
  />
);

// === Button ===
export const Btn = ({ children, onClick, primary, disabled, danger, ghost, style: s = {}, title = "" }) => (
  <MuiButton
    onClick={onClick}
    disabled={disabled}
    title={title}
    variant={primary ? "contained" : ghost ? "text" : "outlined"}
    color={danger ? "error" : "primary"}
    sx={{
      textTransform: "none",
      fontWeight: primary ? 600 : 500,
      whiteSpace: "nowrap",
      borderRadius: "8px",
      letterSpacing: "-.01em",
      ...(ghost && {
        color: "var(--text-2)",
        "&:hover": { color: "var(--text-1)", background: "rgba(255,255,255,.05)" },
      }),
      ...s,
    }}
  >
    {children}
  </MuiButton>
);

// === Clickable Card - glassmorphism style ===
export const Card = ({ children, onClick, style: s = {} }) => (
  <MuiCard
    elevation={0}
    onClick={onClick}
    sx={{
      cursor: onClick ? "pointer" : "default",
      background: "linear-gradient(135deg, rgba(255,255,255,.025) 0%, transparent 60%), var(--surface-1)",
      border: "1px solid var(--border)",
      borderRadius: "8px",
      backgroundImage: "none",
      transition: "all .2s ease",
      "&:hover": onClick ? {
        borderColor: "rgba(75,127,250,.4)",
        transform: "translateY(-2px)",
        boxShadow: "0 8px 32px rgba(75,127,250,.12), 0 0 0 1px rgba(75,127,250,.2)",
      } : {},
      ...s,
    }}
  >
    <CardContent>{children}</CardContent>
  </MuiCard>
);

// === SVG Icon Helper ===
export const SideIcon = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

// === Toast Notification System ===
// Usage: const { toasts, addToast } = useToast();
// addToast("Gespeichert!", "success") / addToast("Fehler!", "error")
// Render <ToastContainer toasts={toasts} /> anywhere in the component tree.

export function useToast() {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const addToast = useCallback((message, type = "success", duration = 3500) => {
    const id = ++idRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);
  return { toasts, addToast };
}

const TOAST_STYLES = {
  success: { bg: "rgba(52,211,153,.14)", border: "rgba(52,211,153,.4)", color: "#34D399", icon: "M20 6L9 17l-5-5" },
  error:   { bg: "rgba(248,113,113,.14)", border: "rgba(248,113,113,.4)", color: "#F87171", icon: "M18 6L6 18M6 6l12 12" },
  info:    { bg: "rgba(75,127,250,.14)", border: "rgba(75,127,250,.4)", color: "#4B7FFA", icon: "M12 16v-4M12 8h.01" },
};

export const ToastContainer = memo(({ toasts }) => {
  if (!toasts || toasts.length === 0) return null;
  return (
    // aria-live: Ohne das bleiben Meldungen fuer Vorleseprogramme unsichtbar - sie stehen nur
    // farbig in der Ecke. "polite" unterbricht nicht, was gerade vorgelesen wird.
    <div aria-live="polite" aria-atomic="false" style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999, display: "flex", flexDirection: "column-reverse", gap: 8, pointerEvents: "none" }}>
      {toasts.map(t => {
        const s = TOAST_STYLES[t.type] || TOAST_STYLES.info;
        return (
          <div key={t.id} role={t.type === "error" ? "alert" : "status"} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 16px", borderRadius: 4,
            background: s.bg, border: `1px solid ${s.border}`,
            backdropFilter: "blur(12px)",
            color: s.color, fontSize: 13, fontWeight: 600, fontFamily: FONT,
            boxShadow: "0 8px 32px rgba(0,0,0,.25)",
            animation: "fadeIn 0.2s ease",
            pointerEvents: "auto",
            maxWidth: 400,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d={s.icon} />
            </svg>
            {t.message}
          </div>
        );
      })}
    </div>
  );
});
